import { Link } from 'react-router-dom';
import { site } from '@shared/site.config';
import DimensionLine from '@/components/DimensionLine';
import BuildingGlyph from '@/components/BuildingGlyph';
import type { GlyphType } from '@/components/BuildingGlyph';
import type { LeadInterest } from '@shared/types';

/**
 * Shared layout for the three service pages.
 *
 * The three are deliberately consistent with each other — they're sibling
 * sheets in one set — while reading differently from the cover sheet, the
 * about sheet and the contact sheet. Variation belongs between sections of
 * the site, not between pages that do the same job.
 */

interface Props {
  sheetNo: string;
  service: { name: string; short: string; summary: string; points: readonly string[] };
  glyph: GlyphType;
  /** Boxed annotations under the summary: property types, lease structures, deal shapes. */
  tags: readonly string[];
  /** Label for the tag row, e.g. "Asset types". */
  tagsLabel: string;
  interest: LeadInterest;
  ctaLabel: string;
  closingLine: string;
  /** Optional drawing note — a scope caution, set in redline. */
  note?: string;
}

export default function ServiceSheet({
  sheetNo,
  service,
  glyph,
  tags,
  tagsLabel,
  interest,
  ctaLabel,
  closingLine,
  note,
}: Props) {
  const tel = `tel:${site.agent.phone.replace(/\D/g, '')}`;

  return (
    <>
      {/* Sheet head */}
      <section className="relative border-b border-medline bg-grid-sheet">
        <span className="sheet-no absolute right-5 top-5 sm:right-8">{sheetNo}</span>

        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
          <div className="grid items-center gap-12 lg:grid-cols-[1.2fr_1fr]">
            <div className="animate-fade-in">
              <p className="annot">{service.short}</p>
              <h1 className="mt-4 font-display text-[42px] font-semibold leading-[1.0] tracking-tight sm:text-[62px]">
                {service.name}
              </h1>
              <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-annot">{service.summary}</p>

              <div className="mt-9">
                <p className="annot">{tagsLabel}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {tags.map((t) => (
                    <span key={t} className="tag">{t}</span>
                  ))}
                </div>
              </div>

              <div className="mt-10 flex flex-wrap gap-3">
                <Link to={`/contact?interest=${interest}`} className="btn-primary">{ctaLabel}</Link>
                <a href={tel} className="btn-ghost">Call {site.agent.phone}</a>
              </div>
            </div>

            <div className="hidden lg:block">
              <BuildingGlyph type={glyph} className="w-full text-medline" />
              <p className="annot mt-5 text-right">Fig. 1 — Elevation, typical</p>
            </div>
          </div>
        </div>
      </section>

      {/* Scope of work, as a numbered specification */}
      <section className="bg-sheet">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
          <DimensionLine label="Scope" className="mb-12" />

          <ol className="max-w-4xl">
            {service.points.map((point, i) => (
              <li
                key={point}
                className="grid grid-cols-[auto_1fr] items-start gap-6 border-t border-hairline py-6 last:border-b last:border-hairline"
              >
                <span className="sheet-no pt-1.5">{String(i + 1).padStart(2, '0')}</span>
                <p className="text-[16px] leading-relaxed text-graphite-600">{point}</p>
              </li>
            ))}
          </ol>

          {note && (
            <div className="mt-10 max-w-3xl border-l-2 border-redline bg-redline-100/50 px-6 py-5">
              <p className="font-mono text-[10px] uppercase tracking-wider2 text-redline-600">Note</p>
              <p className="mt-2 text-[14px] leading-relaxed text-graphite-600">{note}</p>
            </div>
          )}
        </div>
      </section>

      {/* Closing */}
      <section className="border-t border-medline">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16">
          <div className="flex flex-wrap items-end justify-between gap-8">
            <h2 className="max-w-xl font-display text-[28px] leading-tight sm:text-[34px]">{closingLine}</h2>
            <div className="flex flex-wrap gap-3">
              <Link to={`/contact?interest=${interest}`} className="btn-primary">{ctaLabel}</Link>
              <Link to="/about" className="btn-ghost">About Pinnacle Commercial</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
